import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '@/features/auth/services/authService';
import {
  AuthResponse,
  ProviderType,
} from '@/features/auth/types/http/responses/loginResponse';
import { LoginRequest } from '@/features/auth/types/http/requests/loginRequest';
import { User } from '@/types/models/user';
import {
  authUsingProvider,
  forgotPassword,
  loginUser,
  loginUserWithCustomToken,
  logoutUser,
  registerUser,
  resetPassword as resetPasswordService,
  updateUserProfile,
} from '@/features/auth/services/firebaseAuthService';
import { auth } from '@/lib/firebaseConfig';
import { RegisterRequest } from '@/features/auth/types/http/requests/registerRequest';
import { UpdateRequest } from '@/features/auth/types/http/requests/updateRequest';
import { userService } from '@/features/auth/services/userService';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { signIn as nextAuthSignIn, signOut } from 'next-auth/react';
import {
  applyPendingFileMerge,
  bufferAnonFilesForMerge,
} from '@/features/documents/services/fileMergeService';

// Define the initial state
interface AuthState {
  user: User | null;
  loading: boolean;
  changePasswordLoading: boolean;
  error: string | null;
  message?: string | null;
  loggedOut: boolean;
}

interface ProviderAuthPayload {
  user: User;
  isNewUser: boolean;
}

const initialState: AuthState = {
  user: null,
  loggedOut: false,
  loading: false,
  changePasswordLoading: false,
  error: null,
  message: null,
};

// Helper function to format error messages
const formatErrorMessage = (error: any): string => {
  if (!error) return 'An unexpected error occurred. Please try again.';

  // Handle Firebase REST API error format
  if (error.error && error.error.message) {
    const errorCode = error.error.message;

    if (errorCode === 'INVALID_LOGIN_CREDENTIALS') {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (errorCode === 'EMAIL_NOT_FOUND') {
      return "We couldn't find an account with this email address. Please check your email or create a new account.";
    }
    if (errorCode === 'INVALID_PASSWORD') {
      return 'Incorrect password. Please try again or use the "Forgot Password" option.';
    }
    if (errorCode === 'USER_DISABLED') {
      return 'This account has been disabled. Please contact support for assistance.';
    }
    if (errorCode === 'EMAIL_EXISTS') {
      return 'This email address is already in use. Please use a different email or try signing in.';
    }
    if (errorCode === 'OPERATION_NOT_ALLOWED') {
      return 'This sign-in method is not allowed. Please contact support.';
    }
    if (errorCode === 'TOO_MANY_ATTEMPTS_TRY_LATER') {
      return 'Too many failed login attempts. Please try again later or reset your password.';
    }

    // Return the raw error message if no specific handling
    return error.error.message;
  }

  // Handle Firebase auth errors (client SDK format)
  if (error.code) {
    if (error.code === 'auth/email-already-in-use') {
      return 'This email address is already in use. Please use a different email or try signing in.';
    }
    if (error.code === 'auth/user-not-found') {
      return "We couldn't find an account with this email address. Please check your email or create a new account.";
    }
    if (error.code === 'auth/wrong-password') {
      return 'Incorrect password. Please try again or use the "Forgot Password" option.';
    }
    if (error.code === 'auth/invalid-credential') {
      return 'Invalid login credentials. Please check your email and password.';
    }
    if (error.code === 'auth/weak-password') {
      return 'Your password is too weak. Please use a stronger password with at least 6 characters.';
    }
    if (error.code === 'auth/invalid-email') {
      return 'The email address format is invalid. Please enter a valid email.';
    }
    if (error.code === 'auth/too-many-requests') {
      return 'Too many failed login attempts. Please try again later or reset your password.';
    }
    if (error.code === 'auth/network-request-failed') {
      return 'Network error. Please check your internet connection and try again.';
    }
    if (error.code === 'auth/popup-closed-by-user') {
      return 'Sign-in popup was closed before completing the sign in. Please try again.';
    }
    if (error.code === 'auth/requires-recent-login') {
      return 'This operation requires you to re-authenticate. Please sign in again before retrying.';
    }
  }

  // Handle old-style string auth errors
  if (typeof error === 'string') {
    if (error.includes('auth/')) {
      // Extract error code after the auth/ prefix
      const errorCode = error.split('auth/')[1];
      if (errorCode === 'email-already-in-use') {
        return 'This email address is already in use. Please use a different email or try signing in.';
      }
      if (errorCode === 'user-not-found') {
        return "We couldn't find an account with this email address. Please check your email or create a new account.";
      }
      // Add additional error code conditions as needed
    }

    // Return the string error directly if it's already a message
    return error;
  }

  // Handle any error object with a message property
  if (error.message) return error.message;

  return 'An unexpected error occurred. Please try again.';
};

const resolveLoggedInUser = async (uid: string, loginData: unknown) => {
  if ((loginData as Partial<User> | undefined)?.uid) {
    return loginData as User;
  }

  const userResponse = await userService.getUser(uid);

  return userResponse.data;
};

// Async Thunks
export const login = createAsyncThunk(
  'auth/login',
  async (payload: LoginRequest, { rejectWithValue }) => {
    try {
      const oldUid = auth.currentUser?.isAnonymous
        ? auth.currentUser.uid
        : null;
      if (oldUid) await bufferAnonFilesForMerge(oldUid);
      const fbUser = await loginUser(payload);
      if (fbUser) {
        const idToken = await fbUser.getIdToken();
        const response = await authService.login(fbUser.uid, idToken);

        if (oldUid && oldUid !== fbUser.uid) {
          try {
            await authService.migrateFiles(oldUid, fbUser.uid);
          } catch (migrateError) {
            console.error('File migration failed:', migrateError);
          }
          await applyPendingFileMerge(fbUser.uid);
        }

        // nextAuthSignIn('credentials', {
        //   uid: fbUser.uid,
        //   idToken: (response.data as any).token,
        //   redirect: false,
        // })
        //   .then((result) => {
        //     if (result?.error) {
        //       throw new Error(result.error);
        //     }
        //   })
        //   .catch((error) => {
        //     throw new Error(error);
        //   });
        await nextAuthSignIn('credentials', {
          uid: fbUser.uid,
          idToken: (response.data as any).token,
          redirect: false,
        });

        return {
          user: await resolveLoggedInUser(fbUser.uid, response.data),
        };
      }
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error));
    }
  }
);

export const loginWithVerificationCustomToken = createAsyncThunk(
  'auth/loginWithVerificationCustomToken',
  async (customToken: string, { rejectWithValue }) => {
    try {
      const oldUid = auth.currentUser?.isAnonymous
        ? auth.currentUser.uid
        : null;
      if (oldUid) await bufferAnonFilesForMerge(oldUid);

      const fbUser = await loginUserWithCustomToken(customToken);
      const idToken = await fbUser.getIdToken();
      const response = await authService.login(fbUser.uid, idToken);

      if (oldUid && oldUid !== fbUser.uid) {
        try {
          await authService.migrateFiles(oldUid, fbUser.uid);
        } catch (migrateError) {
          console.error('File migration failed:', migrateError);
        }
        await applyPendingFileMerge(fbUser.uid);
      }

      const signInResult = await nextAuthSignIn('credentials', {
        uid: fbUser.uid,
        idToken: (response.data as any).token,
        redirect: false,
      });

      if (signInResult?.error) throw new Error(signInResult.error);

      return { user: await resolveLoggedInUser(fbUser.uid, response.data) };
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error.data?.error || error));
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (payload: RegisterRequest, { rejectWithValue }) => {
    try {
      const oldUid = auth.currentUser?.isAnonymous
        ? auth.currentUser.uid
        : null;
      if (oldUid) await bufferAnonFilesForMerge(oldUid);
      const fbUser = await registerUser(payload);
      if (fbUser) {
        const idToken = await fbUser.getIdToken();

        const response = await authService.register({
          uid: fbUser.uid,
          email: payload.email,
          password: payload.password,
          displayName: payload.displayName,
          idToken,
          isSilent: payload.isSilent,
        });

        if (oldUid && oldUid !== fbUser.uid) {
          try {
            await authService.migrateFiles(oldUid, fbUser.uid);
          } catch (migrateError) {
            console.error('File migration failed:', migrateError);
          }
          await applyPendingFileMerge(fbUser.uid);
        }

        await nextAuthSignIn('credentials', {
          uid: fbUser.uid,
          idToken: (response.data as any).token,
          redirect: false,
        });

        return {
          user: response.data,
        };
      }
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error.data?.error || error));
    }
  }
);

export const providerAuth = createAsyncThunk(
  'auth/providerAuth',
  async (providerType: ProviderType, { rejectWithValue }) => {
    try {
      const oldUid = auth.currentUser?.isAnonymous
        ? auth.currentUser.uid
        : null;
      if (oldUid) await bufferAnonFilesForMerge(oldUid);
      const authResult = await authUsingProvider(providerType);
      if (!authResult || !authResult.user) {
        throw new Error('Failed to authenticate user with Provider');
      }
      const { user, isNewUser } = authResult;
      const accessToken: string = await user.getIdToken();
      const response = await authService.authWithProvider(accessToken);

      if (oldUid && oldUid !== user.uid) {
        try {
          await authService.migrateFiles(oldUid, user.uid);
        } catch (migrateError) {
          console.error('File migration failed:', migrateError);
        }
        await applyPendingFileMerge(user.uid);
      }

      await nextAuthSignIn('credentials', {
        uid: user.uid,
        idToken: (response.data as any).token,
        redirect: false,
      });

      return {
        user: response.data,
        isNewUser: isNewUser,
      };
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error.data?.error || error));
    }
  }
);

export const updateUser = createAsyncThunk(
  'auth/updateUser',
  async (payload: UpdateRequest, { rejectWithValue }) => {
    try {
      if (Object.values(payload).every((value) => value !== null)) {
        await updateUserProfile(payload);
        const response = await authService.updateUser(payload);
        return {
          user: response.data,
        };
      } else {
        throw new Error('Please fill in all required fields.');
      }
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error.data?.error || error));
    }
  }
);

export const refreshUser = createAsyncThunk(
  'auth/refreshUser',
  async (payload: { uid: string }, { rejectWithValue }) => {
    try {
      if (payload.uid) {
        const response = await userService.getUser(payload.uid);
        return {
          user: response.data,
        };
      }
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error.data?.error || error));
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (payload: { uid: string }) => {
    try {
      await authService.logout(payload.uid);
    } catch (error) {
      console.error('Backend logout failed:', error);
    }

    try {
      await logoutUser();
    } catch (error) {
      console.error('Firebase logout failed:', error);
    }

    await signOut({
      redirect: false,
    });

    return true;
  }
);

export const sendPasswordResetEmail = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      await forgotPassword({ email });
      return { message: 'Password reset email has been sent successfully' };
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error));
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (
    { oobCode, newPassword }: { oobCode: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      await resetPasswordService(oobCode, newPassword);
      return { message: 'Password updated successfully 🎉' };
    } catch (error: any) {
      return rejectWithValue(formatErrorMessage(error));
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (
    payload: {
      currentPassword: string;
      newPassword: string;
      fbUser: any;
    },
    { rejectWithValue }
  ) => {
    try {
      const credential = EmailAuthProvider.credential(
        payload.fbUser?.email || '',
        payload.currentPassword
      );

      await reauthenticateWithCredential(payload.fbUser, credential);
      await updatePassword(payload.fbUser, payload.newPassword);

      return { message: 'Password updated successfully.' };
    } catch (error: any) {
      // Handle specific password change errors
      if (error.code === 'auth/invalid-credential') {
        return rejectWithValue(formatErrorMessage(error));
      }
      return rejectWithValue(formatErrorMessage(error));
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload ?? null;
      state.loggedOut = false;
    },
    clearAuthState(state) {
      state.user = null;
      state.error = null;
      state.message = null;
      state.loading = false;
    },
    clearMessageAndError(state) {
      state.error = null;
      state.message = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // Register
    builder.addCase(register.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state, action) => {
      state.user = action.payload?.user ?? null;
      state.loggedOut = false;
      state.loading = false;
    });
    builder.addCase(register.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.user = action.payload?.user ?? null;
      state.loggedOut = false;
      state.loading = false;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    builder.addCase(loginWithVerificationCustomToken.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      loginWithVerificationCustomToken.fulfilled,
      (state, action) => {
        state.user = action.payload.user;
        state.loggedOut = false;
        state.loading = false;
      }
    );
    builder.addCase(
      loginWithVerificationCustomToken.rejected,
      (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      }
    );

    // Provider Auth
    builder.addCase(providerAuth.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      providerAuth.fulfilled,
      (state, action: PayloadAction<ProviderAuthPayload>) => {
        state.user = action.payload.user;
        state.loggedOut = false;
        state.loading = false;
      }
    );
    builder.addCase(providerAuth.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update User
    builder.addCase(updateUser.pending, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.loading = false;
    });
    builder.addCase(updateUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Refresh User
    builder.addCase(refreshUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(refreshUser.fulfilled, (state, action) => {
      state.user = action.payload?.user ?? null;
      state.loading = false;
    });
    builder.addCase(refreshUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Logout
    builder.addCase(logout.pending, (state) => {
      state.loading = true;
      state.user = null;
      state.loggedOut = true;
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.loggedOut = true;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(logout.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    builder.addCase(sendPasswordResetEmail.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.message = null;
    });
    builder.addCase(sendPasswordResetEmail.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload.message;
    });
    builder.addCase(sendPasswordResetEmail.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.message = null;
    });

    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.message = null;
    });
    builder.addCase(resetPassword.fulfilled, (state, action) => {
      state.loading = false;
      state.message = action.payload.message;
      state.error = null;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.message = null;
    });

    builder.addCase(changePassword.pending, (state) => {
      state.changePasswordLoading = true;
      state.error = null;
      state.message = null;
    });
    builder.addCase(changePassword.fulfilled, (state, action) => {
      state.changePasswordLoading = false;
      state.message = action.payload.message;
      state.error = null;
    });
    builder.addCase(changePassword.rejected, (state, action) => {
      state.changePasswordLoading = false;
      state.error = action.payload as string;
      state.message = null;
    });
  },
});

// Actions
export const { setUser, clearAuthState, clearMessageAndError } =
  authSlice.actions;

// Selector
export const selectAuth = (state: { auth: AuthState }) => state.auth;

// Reducer
export default authSlice.reducer;
